import * as React from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useMediaQuery } from '@material-ui/core';
import Head from 'next/head';
import Image from 'next/legacy/image';

import { getImageUrl, getThumbnailUrl } from 'lib/util/imageUrl';
import { getBreadcrumbsLDJSONString } from 'lib/util/getBreadcrumbsLDJSONObject';
import { TourList } from 'components/PrivateMarketplace/TourList/TourList';
import { Breadcrumbs } from 'components/PrivateMarketplace/Breadcrumbs/Breadcrumbs';
import {
  selectCategories,
  selectFeatures,
  selectHeaderDescriptionText,
} from 'lib/util/privateMarketplace';
import { selectProductSummaries, ProductSummaryShape } from 'lib/util/productSummaryShape';
import { getThemeCss } from 'components/Themes';
import { BookingWidgetThemeContext } from 'contexts/BookingWidgetThemeContext';
import { getThemeCssOverride } from 'components/Themes';
import { appendQueryString } from 'lib/util/appendQueryString';
import { useQueryString } from 'hooks/useQueryString';

type Props = {
  host: string;
};

export const ProductCollection = ({ host }: Props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const name = decodeURIComponent(router.query.categoryFeatureName as string);
  const bookingWidgetTheme = React.useContext(BookingWidgetThemeContext);

  const [viewMore, setViewMore] = React.useState<boolean>(false);

  const categories = useSelector(selectCategories);
  const features = useSelector(selectFeatures);
  const allProducts = useSelector(selectProductSummaries);
  const headerText = useSelector(selectHeaderDescriptionText);

  let filterTypes: ('CATEGORY' | 'FEATURE' | 'TAG')[] = ['FEATURE', 'TAG'];
  let category = categories.find((c) => c.name === name);
  if (!category) {
    category = features.find((f) => f.name === name);
    filterTypes = ['CATEGORY', 'TAG'];
  }

  const pageDisplayName = category?.display_name || category?.name || '';

  const bannerImageUrl = category?.banner_image_url ?? '';

  const categoryProducts: ProductSummaryShape[] = [];
  for (const productId of category?.product_ids || []) {
    const product = allProducts.find((product) => product.id === productId);
    if (product) {
      categoryProducts.push(product);
    }
  }

  const queryString = useQueryString();

  const screenWidthIsLessThan768 = useMediaQuery('(max-width: 768px)');
  const highlightsCount = category?.highlights?.length || 0;
  const showViewMoreButton = screenWidthIsLessThan768 && highlightsCount > 1;

  const title = `${pageDisplayName || name} | ${headerText}`;

  if (!category) {
    return (
      <>
        <Head>
          <title>{title}</title>
        </Head>
        <Breadcrumbs
          pageTitle={name}
          parentLinks={[
            {
              href: appendQueryString('/top', queryString),
              text: t('HOME'),
            },
          ]}
        />
        <div>{t('Category Not Found')}</div>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={category.description} />
        <meta property="og:title" content={pageDisplayName} />
        <meta property="og:site_name" content={headerText} />
        {category.description && <meta property="og:description" content={category.description} />}
        {bannerImageUrl && <meta property="og:image" content={bannerImageUrl} />}
        <meta property="og:url" content={'TODO'} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: getBreadcrumbsLDJSONString(host, [
              {
                name: t('HOME'),
                relativeLink: '/top',
              },
              {
                name: pageDisplayName,
              },
            ]),
          }}
        />
      </Head>
      <Breadcrumbs
        pageTitle={pageDisplayName}
        parentLinks={[
          {
            href: appendQueryString('/top', queryString),
            text: t('HOME'),
          },
        ]}
      />

      <div
        className={clsx('base-feature', getThemeCssOverride(bookingWidgetTheme, 'base-feature'))}
      >
        <Image
          className="base-feature__pic"
          layout="fill"
          src={bannerImageUrl}
          loader={({ src, width }) => getImageUrl(src, width)}
          alt="banner"
        />
        <div className="base-feature__body">
          <h1
            className={clsx(
              'base-feature__body__headline',
              getThemeCss(bookingWidgetTheme, 'base-font')
            )}
          >
            {category.display_name || category.name}
          </h1>
          <p className="base-feature__body__description">{category.description}</p>
        </div>
      </div>

      {category.highlights.length > 0 && (
        <div className={clsx('base-point', getThemeCssOverride(bookingWidgetTheme, 'base-point'))}>
          <div className="base-point__inner">
            <h2
              className={clsx('base-point__headline', getThemeCss(bookingWidgetTheme, 'base-font'))}
            >
              {t('Highlights')}
            </h2>
            <div
              className={clsx({
                'base-point__body': true,
                'is-close': !viewMore,
                'is-open': viewMore,
              })}
            >
              <ul className="base-point__list">
                {category.highlights.map((highlight, idx) => (
                  <li key={idx} className="base-point__list__item">
                    <div className="base-point__list__item__pic">
                      <img
                        src={highlight.thumbnail_url && getThumbnailUrl(highlight.thumbnail_url)}
                        alt="feature"
                      />
                      <div className="base-point__list__item__pic__label maincolor-bg">
                        <span>{t('POINT')}</span>
                        <span>{idx + 1}</span>
                      </div>
                    </div>
                    <h3 className="base-point__list__item__ttl maincolor-txt">{highlight.title}</h3>
                    <p className="base-point__list__item__description">{highlight.description}</p>
                  </li>
                ))}
              </ul>
              {showViewMoreButton && (
                <div
                  className={clsx({
                    'base-point__more': true,
                    'is-close': !viewMore,
                    'is-open': viewMore,
                  })}
                >
                  <a
                    onClick={() => setViewMore(!viewMore)}
                    className="base-point__more__btn maincolor-txt maincolor-border"
                  >
                    {viewMore ? t('Show Less') : t('Show More')}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <TourList
        products={categoryProducts}
        filterTypes={filterTypes}
        displayLabelTypes={['RECOMMENDED', 'RANKING_GENERAL', 'PROMOTION']}
      />
    </>
  );
};
